import { create } from "zustand";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  kind: "success" | "error" | "info";
  message: string;
  action?: ToastAction;
  duration?: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function scheduleDismiss(id: number, duration: number, dismiss: (id: number) => void): void {
  const existing = timers.get(id);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    timers.delete(id);
    dismiss(id);
  }, duration);
  timers.set(id, timer);
}

export const useToast = create<ToastState>((set, get) => ({
  toasts: [],
  push: (toast) => {
    const id = nextId++;
    const duration = toast.duration ?? 4000;
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }].slice(-5) }));
    if (!toast.action) {
      scheduleDismiss(id, duration, get().dismiss);
    }
    return id;
  },
  dismiss: (id) => {
    const existing = timers.get(id);
    if (existing) {
      clearTimeout(existing);
      timers.delete(id);
    }
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

export function toast(kind: Toast["kind"], message: string): void {
  useToast.getState().push({ kind, message });
}

/** Deferred delete: optimistic UI + 5s undo window before commit runs. */
export function showDeleteUndoToast(
  message: string,
  opts: {
    onUndo: () => void;
    onCommit: () => void | Promise<void>;
    onCommitError?: (err: unknown) => void;
  },
): void {
  let settled = false;

  const id = useToast.getState().push({
    kind: "success",
    message,
    duration: 5000,
    action: {
      label: "Undo",
      onClick: () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        useToast.getState().dismiss(id);
        opts.onUndo();
      },
    },
  });

  const timer = setTimeout(async () => {
    if (settled) return;
    settled = true;
    useToast.getState().dismiss(id);
    try {
      await opts.onCommit();
    } catch (err) {
      opts.onUndo();
      opts.onCommitError?.(err);
    }
  }, 5000);
}
